import { useAppDispatch } from '_store';
import {
  setHeatmapActiveAction,
  setHeatmapBaseRangeAction,
  setHeatmapDateRangeAction,
  setHeatmapGranularityAction,
  setHeatmapScaleToVisibleDataAction,
  vehicleCountsRequestAction,
} from '_store/heatmap/heatmapReducers';
import { heatmapSelector } from '_store/heatmap/selectors';
import {
  bucketSeries,
  defaultGranularity,
  getDataset,
  getVehicleCounts,
  GRANULARITIES,
  heatmapKey,
  MS_DAY,
  MS_HOUR,
  MS_QUARTER_HOUR,
  round2,
  vehicleKey,
  vehicleSeries,
} from '_store/heatmap/utils';
import { useLocalization } from '@fluent/react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Switch,
  Typography,
} from '@material-ui/core';
import { fade, makeStyles, useTheme } from '@material-ui/core/styles';
import Pause from '@material-ui/icons/Pause';
import PlayArrow from '@material-ui/icons/PlayArrow';
import Refresh from '@material-ui/icons/Refresh';
import SkipNext from '@material-ui/icons/SkipNext';
import SkipPrevious from '@material-ui/icons/SkipPrevious';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { addDays, endOfDay, format, startOfDay, subDays } from 'date-fns';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

import DatePicker from '../../items/DatePicker';
import DayComparison from './DayComparison';

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2, 1.5),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 2,
  },
  label: { color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5) },
  dates: { display: 'flex', gap: theme.spacing(2), '& > *': { flex: 1, minWidth: 0 } },
  row: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  chart: { position: 'relative', width: '100%', height: 240 },
}));

const PERIODS = [
  { id: '1h', ms: MS_HOUR },
  { id: '6h', ms: 6 * MS_HOUR },
  { id: '24h', ms: MS_DAY },
  { id: '7d', ms: 7 * MS_DAY },
  { id: '30d', ms: 30 * MS_DAY },
];
type Period = string; // a PERIODS id or 'custom'
const GRANULARITY_KEY: Record<number, string> = {
  [MS_QUARTER_HOUR]: '15m',
  [MS_HOUR / 2]: '30m',
  [MS_HOUR]: '1h',
  [MS_DAY]: '1day',
};
const PLAY_INTERVAL_MS = 800;

const ceilTo = (t: number, step: number) => Math.ceil(t / step) * step;
const floorTo = (t: number, step: number) => Math.floor(t / step) * step;

export const HeatmapControls: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const { active, scaleToVisibleData, dataKey, status, vehicleCounts } =
    useSelector(heatmapSelector);

  const [period, setPeriod] = useState<Period>('24h');
  const [now, setNow] = useState(() => Date.now()); // "last N" is anchored here, Refresh moves it
  const [customStart, setCustomStart] = useState<Date | null>(() => subDays(new Date(), 7));
  const [customEnd, setCustomEnd] = useState<Date | null>(() => new Date());
  const [userGranularity, setUserGranularity] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null); // bar index, null = whole interval
  const [playing, setPlaying] = useState(false);

  // Quarter-hour aligned, because that is the API's resolution: a 15 min slot is exactly one sample
  const range = useMemo((): [number, number] | null => {
    if (period === 'custom') {
      if (!customStart || !customEnd) return null;
      const start = startOfDay(customStart).getTime();
      const end = addDays(startOfDay(customEnd), 1).getTime();
      return start < end ? [start, end] : null;
    }
    const ms = PERIODS.find((p) => p.id === period)?.ms ?? MS_DAY;
    const end = ceilTo(now, MS_QUARTER_HOUR);
    return [floorTo(end - ms, MS_QUARTER_HOUR), end];
  }, [period, now, customStart, customEnd]);
  const [rangeStart, rangeEnd] = range ?? [0, 0];

  // Default follows the interval; a manual choice sticks until the interval changes
  const granularity = userGranularity ?? defaultGranularity(rangeEnd - rangeStart);
  useEffect(() => {
    setUserGranularity(null);
    setSelected(null);
    setPlaying(false);
  }, [rangeStart, rangeEnd]);
  useEffect(() => {
    setSelected(null);
  }, [granularity]);
  useEffect(() => {
    dispatch(setHeatmapGranularityAction(granularity));
  }, [granularity, dispatch]);

  // The bars of the chart are the slots of the map
  // Bars sit on multiples of the granularity (UTC, like the API's buckets); the first/last are clamped to the range
  const origin = Math.floor(rangeStart / granularity) * granularity;
  const barCount = range ? Math.ceil((rangeEnd - origin) / granularity) : 0;
  const win: [number, number] | null = useMemo(() => {
    if (!range) return null;
    if (selected === null || selected >= barCount) return [rangeStart, rangeEnd];
    const start = origin + selected * granularity;
    return [Math.max(rangeStart, start), Math.min(rangeEnd, start + granularity)];
  }, [range, rangeStart, rangeEnd, selected, barCount, granularity, origin]);

  useEffect(() => {
    if (range) dispatch(setHeatmapBaseRangeAction([rangeStart, rangeEnd]));
  }, [rangeStart, rangeEnd, dispatch]);

  useEffect(() => {
    if (range) dispatch(vehicleCountsRequestAction({ start: rangeStart, end: rangeEnd }));
  }, [rangeStart, rangeEnd, dispatch]);

  useEffect(() => {
    if (win) dispatch(setHeatmapDateRangeAction(win));
  }, [win?.[0], win?.[1], dispatch]);

  useEffect(() => {
    if (!playing || barCount < 2) return undefined;
    const id = window.setInterval(
      () => setSelected((i) => (i === null ? 0 : (i + 1) % barCount)),
      PLAY_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [playing, barCount]);

  const metric = active === 'speed_value' ? 'speed_value' : 'counter';
  const ds = range && dataKey === heatmapKey(metric, rangeStart, rangeEnd) ? getDataset(dataKey) : null;
  const bucketMs = granularity;
  const vcReady = range && vehicleCounts.key === vehicleKey(rangeStart, rangeEnd);
  const vc = vcReady ? getVehicleCounts(vehicleCounts.key) : null;
  const hasData = metric === 'counter' ? !!vc : status === 'ready' && !!ds;
  const dataStatus = metric === 'counter' ? vehicleCounts.status : status;

  const chartData = useMemo(() => {
    let series: { starts: number[]; values: number[] } = { starts: [], values: [] };
    if (metric === 'counter') {
      if (vc) series = vehicleSeries(vc, rangeStart, rangeEnd, bucketMs);
    } else if (ds) series = bucketSeries(ds, origin, rangeEnd, bucketMs);
    const { starts, values } = series;
    const full = theme.palette.primary.main;
    const dim = fade(full, 0.3);
    const highlight = selected !== null && win;
    let labelFormat = 'HH:mm';
    if (bucketMs === MS_DAY) labelFormat = 'd MMM';
    else if (rangeEnd - rangeStart > 2 * MS_DAY) labelFormat = 'd MMM HH:mm';
    return {
      labels: starts.map((t) => format(new Date(t), labelFormat)),
      datasets: [
        {
          data: values,
          backgroundColor: starts.map((t) =>
            !highlight || (t < win[1] && t + bucketMs > win[0]) ? full : dim,
          ),
        },
      ],
    };
  }, [ds, vc, metric, origin, rangeStart, rangeEnd, bucketMs, selected, win, theme]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      onClick: (_e: unknown, elements: Array<{ index: number }>) => {
        if (elements.length > 0) {
          setPlaying(false);
          setSelected((cur) => (cur === elements[0].index ? null : elements[0].index));
        }
      },
      onHover: (e: { native?: Event | null }, els: unknown[]) => {
        const target = e.native?.target as HTMLElement | undefined;
        if (target) target.style.cursor = els.length > 0 ? 'pointer' : 'default';
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (c: { dataset: { label?: string }; parsed: { y: number } }) =>
              `${c.dataset.label}: ${round2(c.parsed.y)}`,
          },
        },
      },
      scales: {
        x: { ticks: { color: theme.palette.text.secondary, maxRotation: 45 }, grid: { display: false } },
        y: {
          beginAtZero: true,
          ticks: { color: theme.palette.text.secondary, callback: (v: string | number) => round2(Number(v)) },
          grid: { color: theme.palette.divider },
          title: {
            display: true,
            color: theme.palette.text.secondary,
            text: l10n.getString(metric === 'speed_value' ? 'heatmap-chart-y-speed' : 'heatmap-chart-y-counter'),
          },
        },
      },
    }),
    [theme, l10n, metric],
  );

  const slotLabel = useMemo(() => {
    if (!win) return '';
    const [s, e] = [new Date(win[0]), new Date(win[1])];
    const sameDay = s.toDateString() === new Date(win[1] - 1).toDateString();
    return `${format(s, 'd MMM yyyy, HH:mm')} – ${format(e, sameDay ? 'HH:mm' : 'd MMM yyyy, HH:mm')}`;
  }, [win]);

  const t = (id: string) => l10n.getString(id);
  return (
    <Box className={classes.root}>
      <div>
        <Typography variant="body2" className={classes.label}>
          {t('heatmap-metric')}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={active}
          onChange={(_, v) => v && dispatch(setHeatmapActiveAction(v))}
        >
          <ToggleButton value="counter">{t('heatmap-metric-counter')}</ToggleButton>
          <ToggleButton value="speed_value">{t('heatmap-metric-speed')}</ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div>
        <Typography variant="body2" className={classes.label}>
          {t('heatmap-period')}
        </Typography>
        <div className={classes.row}>
          <Select
            variant="outlined"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value as Period);
              setNow(Date.now());
            }}
            style={{ flex: 1 }}
          >
            {PERIODS.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {t(`heatmap-period-${p.id}`)}
              </MenuItem>
            ))}
            <MenuItem value="custom">{t('heatmap-period-custom')}</MenuItem>
          </Select>
          {period !== 'custom' && (
            <IconButton aria-label={t('heatmap-refresh')} onClick={() => setNow(Date.now())}>
              <Refresh />
            </IconButton>
          )}
        </div>
      </div>

      {period === 'custom' && (
        <div className={classes.dates}>
          <DatePicker
            label={t('dashboard-heatmap-start-date')}
            value={customStart}
            onChange={setCustomStart}
            maxDate={customEnd ?? undefined}
            disablePast={false}
            style={{ marginTop: 0 }}
          />
          <DatePicker
            label={t('dashboard-heatmap-end-date')}
            value={customEnd}
            onChange={setCustomEnd}
            minDate={customStart ?? undefined}
            maxDate={endOfDay(new Date())}
            disablePast={false}
            style={{ marginTop: 0 }}
          />
        </div>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={scaleToVisibleData}
            onChange={(e) => dispatch(setHeatmapScaleToVisibleDataAction(e.target.checked))}
            color="primary"
          />
        }
        label={<Typography variant="body2">{t('heatmap-scale-switch')}</Typography>}
      />

      <div>
        <Typography variant="body2" className={classes.label}>
          {t('heatmap-granularity')}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={granularity}
          onChange={(_, v) => v && setUserGranularity(v)}
        >
          {GRANULARITIES.map((g) => (
            <ToggleButton key={g} value={g}>
              {t(`heatmap-granularity-${GRANULARITY_KEY[g]}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <div>
        <Typography variant="body2" className={classes.label}>
          {t(metric === 'speed_value' ? 'heatmap-chart-title-speed' : 'heatmap-chart-title-counter')}
        </Typography>
        {hasData && chartData.labels.length > 0 ? (
          <div className={classes.chart}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        ) : (
          <Typography variant="caption" color="textSecondary">
            {t(dataStatus === 'loading' ? 'heatmap-loading' : 'dashboard-heatmap-no-data')}
          </Typography>
        )}
      </div>

      <Typography variant="body2" color="primary">
        {slotLabel}
      </Typography>

      {barCount > 1 && (
        <div>
          <div className={classes.row}>
            <IconButton
              size="small"
              aria-label={t('heatmap-previous')}
              disabled={selected === null || selected === 0}
              onClick={() => setSelected((selected ?? 0) - 1)}
            >
              <SkipPrevious />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t(playing ? 'forms-settings-heatmap-pause' : 'forms-settings-heatmap-play')}
              onClick={() => setPlaying((p) => !p)}
            >
              {playing ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton
              size="small"
              aria-label={t('heatmap-next')}
              disabled={selected === barCount - 1}
              onClick={() => setSelected(selected === null ? 0 : selected + 1)}
            >
              <SkipNext />
            </IconButton>
            <Button size="small" disabled={selected === null} onClick={() => setSelected(null)}>
              {t('heatmap-whole-interval')}
            </Button>
          </div>
          <Slider
            value={selected ?? 0}
            min={0}
            max={barCount - 1}
            step={1}
            onChange={(_, v) => {
              setPlaying(false);
              setSelected(v as number);
            }}
          />
        </div>
      )}

      <DayComparison followSlot={selected !== null && win ? win : null} />
    </Box>
  );
};

export default HeatmapControls;
